import { createSelector } from '@reduxjs/toolkit';
import {
  getLegacyDaypartsAvailability,
  getLegacyDaypartString,
  getDaypartAvailability,
  getDaypartString,
} from '../utils/ordering';
import { getMenu, getCategoryAvailabilityById } from './';
import getOrderDateTimeSelected from './getOrderDateTimeSelected';
import getDayparts from './getDayparts';
import { isUsingLegacyDayparts } from '../utils/ordering/daypartMenuAvailability';

/**
 * # Get Item Availability
 * This is used in each of the <MenuListItems/> on various platforms
 *
 * - If this returns:
 *  - false -> render nothing
 *  - true -> render the actual component
 *
 * ## Note:
 * If you are looking for what decides if a section of the menu
 * gets rendered based on the availability of a section look at:
 * rootSubCategoriesFilteredByAvailability in
 * getFilteredRootCategory
 *
 */
export default (item: Item, mimCategoryId?: string, dayInFull?: boolean) =>
  createSelector(
    [
      getMenu,
      getCategoryAvailabilityById(mimCategoryId || ''),
      getOrderDateTimeSelected,
      getDayparts,
    ],
    (
      menu,
      [categoryAvailability, categoryAvailabilityString],
      dateTimeSelected,
      daypartSlots,
    ): [boolean, string] => {
      if (!item || !menu) return [false, ''];

      // If the category this item belongs to is unavailable, then the item is
      // unavailable without needing to check the item's availability.
      if (!categoryAvailability) {
        return [false, categoryAvailabilityString];
      }

      const {
        dayparts,

        // Legacy fields below:
        availability,
        start,
        end,
        days,
      } = item;

      if (isUsingLegacyDayparts(dayparts)) {
        return [
          getLegacyDaypartsAvailability({
            availability,
            days,
            start,
            end,
            dateTimeSelected,
          }),
          getLegacyDaypartString(availability, days, start, end, dayInFull),
        ];
      }

      const isAvailable = getDaypartAvailability({
        dayparts,
        daypartSlots,
        dateTimeSelected,
      });
      const availabilityString = getDaypartString({
        dayparts,
        daypartSlots,
        dayInFull,
      });

      return [isAvailable, availabilityString];
    },
  );
